import { Archive, Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import { BreadcrumbItem } from '@/components/ui/breadcrumb'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useIdCompany } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  ObjectStorageSwitcherQuery,
  ObjectStorageSwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/object-storage/object-storage-switcher/switcher.types.gen'

const OBJECT_STORAGE_SWITCHER_QUERY = gql`
  query ObjectStorageSwitcher($idCompany: String!) {
    company(id: $idCompany) {
      id
      objectStorages @live {
        id
        total
        items {
          id
          displayName
        }
      }
    }
  }
`

export const ObjectStorageSwitcherData = () => {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { idObjectStorage = '' } = useParams()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    ObjectStorageSwitcherQuery,
    ObjectStorageSwitcherQueryVariables
  >(OBJECT_STORAGE_SWITCHER_QUERY, {
    variables: { idCompany },
  })

  const objectStorages = data?.company?.objectStorages?.items ?? []

  const activeObjectStorage =
    objectStorages.find(
      (objectStorage) => objectStorage.id === idObjectStorage
    ) || objectStorages[0]

  return (
    <BreadcrumbItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className="cursor-pointer">
          <SidebarMenuButton
            size="lg"
            aria-expanded={open}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
          >
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span
                title={activeObjectStorage?.displayName ?? ''}
                className="max-w-[250px] truncate text-foreground font-medium"
              >
                {activeObjectStorage?.displayName ?? ''}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command>
            <CommandInput
              placeholder="Search objectStorage..."
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>No object storage found.</CommandEmpty>
              <CommandGroup>
                {objectStorages.map((objectStorage) => (
                  <Link
                    key={objectStorage.id}
                    to={`${pathname.replace(idObjectStorage, objectStorage.id)}${pathname.endsWith('/') ? '' : '/'}?idCompany=${idCompany}`}
                    onClick={() => setOpen(false)}
                  >
                    <CommandItem
                      value={objectStorage.id}
                      keywords={[objectStorage.displayName]}
                      className="cursor-pointer"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border">
                        {activeObjectStorage?.id === objectStorage.id ? (
                          <Check className="size-3.5 shrink-0" />
                        ) : (
                          <Archive className="size-3.5 shrink-0" />
                        )}
                      </div>
                      <span
                        className={cn('truncate', {
                          'font-medium':
                            activeObjectStorage?.id === objectStorage?.id,
                        })}
                      >
                        {objectStorage.displayName}
                      </span>
                    </CommandItem>
                  </Link>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </BreadcrumbItem>
  )
}
